// Inbox-view voor verwerkte e-mails. PRD-08 §3.11 + PRD-03 §3.9.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface Email {
  id: string;
  subject: string | null;
  fromAddress: string;
  fromName: string | null;
  bodySnippet: string | null;
  receivedAt: string;
  emailType: string | null;
  threatLevel: "NONE" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" | null;
  typeConfidence: number | null;
  extractedCreditor: string | null;
  extractedAmount: string | null;
  extractedDueDate: string | null;
  processingStatus: string;
}

const THREAT_COLORS: Record<string, string> = {
  NONE: "bg-neutral-100 text-neutral-700",
  LOW: "bg-blue-50 text-blue-800",
  MEDIUM: "bg-amber-50 text-amber-800",
  HIGH: "bg-orange-50 text-orange-800",
  CRITICAL: "bg-red-50 text-red-800 font-semibold",
};

export function EmailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Email["threatLevel"] | "ALL">("ALL");

  const emailsQuery = useQuery({
    queryKey: ["emails", filter],
    queryFn: () =>
      api.get<{ emails: Email[] }>(
        `/v1/emails${filter !== "ALL" ? `?threatLevel=${filter}` : ""}`
      ),
    refetchInterval: 30_000,
  });

  return (
    <main className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold">{t("email.inbox.title", "Inbox")}</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm text-neutral-600 hover:text-neutral-900"
          >
            ← {t("back", "Terug")}
          </button>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4 overflow-x-auto">
          {(["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
                filter === f
                  ? "bg-brand-500 text-white"
                  : "bg-white border border-neutral-200 text-neutral-700"
              }`}
            >
              {f === "ALL" ? t("email.inbox.all", "Alle") : f}
            </button>
          ))}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          {emailsQuery.isLoading ? (
            <p className="p-6 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
          ) : emailsQuery.data?.emails.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">
              <p className="mb-2">{t("email.inbox.empty", "Nog geen verwerkte e-mails.")}</p>
              <Link to="/settings/email" className="text-brand-600 hover:text-brand-800">
                {t("email.inbox.connect", "Account koppelen →")}
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {emailsQuery.data?.emails.map((email) => (
                <li key={email.id} className="px-5 py-3 hover:bg-neutral-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {email.threatLevel && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${THREAT_COLORS[email.threatLevel]}`}
                          >
                            {email.threatLevel}
                          </span>
                        )}
                        {email.emailType && (
                          <span className="text-xs text-neutral-500 font-mono">
                            {email.emailType}
                          </span>
                        )}
                        {email.typeConfidence != null && email.typeConfidence < 0.8 && (
                          <span className="text-xs text-amber-700">
                            {Math.round(email.typeConfidence * 100)}% conf
                          </span>
                        )}
                        {email.processingStatus === "MANUAL_REVIEW" && (
                          <span className="text-xs text-amber-700">⚑ review</span>
                        )}
                      </div>
                      <p className="font-medium text-sm truncate">
                        {email.subject ?? t("email.inbox.noSubject", "(geen onderwerp)")}
                      </p>
                      <p className="text-xs text-neutral-600 truncate">
                        {email.fromName ? `${email.fromName} <${email.fromAddress}>` : email.fromAddress}
                      </p>
                      {email.bodySnippet && (
                        <p className="text-xs text-neutral-500 mt-1 line-clamp-1">
                          {email.bodySnippet}
                        </p>
                      )}
                      {(email.extractedCreditor || email.extractedAmount) && (
                        <div className="mt-2 flex gap-3 text-xs">
                          {email.extractedCreditor && (
                            <span className="text-neutral-700">
                              <strong>{email.extractedCreditor}</strong>
                            </span>
                          )}
                          {email.extractedAmount && (
                            <span className="text-neutral-700">€ {email.extractedAmount}</span>
                          )}
                          {email.extractedDueDate && (
                            <span className="text-neutral-500">
                              {t("email.inbox.due", "Verloopt")}:{" "}
                              {new Date(email.extractedDueDate).toLocaleDateString("nl-NL")}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <time className="text-xs text-neutral-400 flex-shrink-0">
                      {new Date(email.receivedAt).toLocaleDateString("nl-NL", {
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </main>
  );
}
